import dayjs from "dayjs"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import { getAllContractors } from "../../../service/contractor"
import Table from "../../../core/common/dataTable/index"
import PredefinedDateRanges from "../../../core/common/datePicker"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import {
  cancelTransportInvoice,
  FeeBreakdownItem,
  getTransportInvoiceSummary,
  getTransportInvoices,
  getTransportInvoicesForPrint,
  MonthBreakdownItem,
} from "../../../service/fee-invoices"
import { all_routes } from "../../router/all_routes"
import { toast } from "react-toastify"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { ConfirmationModal } from "../../../components"
import { selectUser } from "../../../core/data/redux/slice/user"
import { SYSTEM_ROLES } from "../../../constant"
import { isSuperAdmin } from "../../../utils/rbacHelper"
import { InvoiceTableData } from "../../accounts/accounts-invoices"
import { getTransportRemainingMonths } from "../../../service/discount"
import TransportInvoicePrintModal from "./transportInvoicePrintModal"
import {
  printTable,
  exportTableToPdf,
  exportTableToExcel,
} from "../../../utils/exportTable"

const TRANSPORT_SORT_OPTIONS = [
  {
    label: "Newest First",
    value: { sortBy: "createdAt", sortOrder: "desc" },
  },
  {
    label: "Oldest First",
    value: { sortBy: "createdAt", sortOrder: "asc" },
  },
  {
    label: "Name (A-Z)",
    value: { sortBy: "studentName", sortOrder: "asc" },
  },
  {
    label: "Name (Z-A)",
    value: { sortBy: "studentName", sortOrder: "desc" },
  },
]

interface PaginationDataType {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

interface TransportInvoiceSummary {
  totalAmount?: number
  totalCollected: number
  totalDiscount: number
  totalFine: number
  totalOnline: number
  totalCash: number
  totalPending?: number
  totalDue?: number
  feeBreakdown: FeeBreakdownItem[]
  monthBreakdownData: MonthBreakdownItem[]
}

const DEFAULT_SUMMARY: TransportInvoiceSummary = {
  totalAmount: 0,
  totalCollected: 0,
  totalDiscount: 0,
  totalFine: 0,
  totalOnline: 0,
  totalCash: 0,
  feeBreakdown: [],
  monthBreakdownData: [],
}

const TransportInvoices = () => {
  const routes = all_routes
  const systems = useSelector(selectSystemsForCurrentBranch)
  const currentBranch = useSelector(selectCurrentBranch)
  const currentSession = useSelector(selectCurrentSession)
  const user = useSelector(selectUser)

  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState("")
  const [deleteInvoiceId, setDeleteInvoiceId] = useState("")
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null)
  const [paginationData, setPaginationData] = useState<PaginationDataType>()
  const [pagination, setPagination] = useState({ page: 1, limit: 10 })

  const [contractorOptions, setContractorOptions] = useState<Option[]>([])

  const ALL_CONTRACTOR: Option = { value: "", label: "All Contractors" }
  const allContractorOptions = [ALL_CONTRACTOR, ...contractorOptions]

  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string
    status?: string
    system?: string
    session?: string
    branch?: string
    startDate?: string
    endDate?: string
    contractor?: string
  }>({
    system: systems[0]?._id,
    session: (currentSession as any)?._id,
    startDate: currentSession?.startDate,
    endDate: currentSession?.endDate,
  })

  // Reset date filter and remount picker when session changes
  useEffect(() => {
    setAppliedFilters(prev => ({
      ...prev,
      session: (currentSession as any)?._id,
      startDate: currentSession?.startDate,
      endDate: currentSession?.endDate,
    }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [(currentSession as any)?._id])

  const [summary, setSummary] = useState<TransportInvoiceSummary>(DEFAULT_SUMMARY)
  const [sortBy, setSortBy] = useState(TRANSPORT_SORT_OPTIONS[0])
  const [cancellingInvoice, setCancellingInvoice] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)

  const EXPORT_HEADERS = [
    "Invoice Number",
    "Student Name",
    "GR No",
    "Contractor",
    "Fees Group",
    "Month",
    "Date",
    "Sub Total (RS)",
    "Discount (RS)",
    "Paid Amount (RS)",
    "Status",
  ]

  const toExportRows = (data: any[]): string[][] =>
    data.map(inv => {
      const name = inv.student?.userId
        ? `${inv.student.userId.firstName || ""} ${inv.student.userId.lastName || ""}`.trim()
        : "N/A"
      const months = (inv.invoiceItems || [])
        .map((item: any) => {
          const m = item.month
          if (!m || m === "other") return null
          return dayjs(m).isValid() ? dayjs(m).format("MMMM YYYY") : m
        })
        .filter(Boolean)
        .join(", ") || "–"
      const discount = (inv.invoiceItems || []).reduce(
        (sum: number, item: any) => sum + Number(item?.discount || 0),
        0,
      )
      return [
        inv.invoiceNumber || inv._id || "–",
        name || "N/A",
        inv.student?.grNumber ?? "–",
        inv.contractor?.name || "–",
        inv.feeGroupId?.name || "Transport",
        months,
        inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : "–",
        String(inv.subTotal ?? "–"),
        String(discount || "–"),
        String(inv.total ?? "–"),
        inv.status || "–",
      ]
    })

  const fetchAllForExport = async (): Promise<any[]> => {
    const response = await getTransportInvoicesForPrint({
      status: appliedFilters?.status,
      class: appliedFilters?.class,
      system: appliedFilters?.system,
      sessionId: appliedFilters?.session,
      branch: appliedFilters?.branch,
      startDate: appliedFilters?.startDate,
      endDate: appliedFilters?.endDate,
      contractorId: appliedFilters?.contractor || undefined,
      sortBy: sortBy.value.sortBy,
      sortOrder: sortBy.value.sortOrder,
    })
    return response?.data || []
  }

  useEffect(() => {
    getAllContractors({ page: 1, limit: 500, status: "active" })
      .then((res: any) => {
        const list = (res?.data ?? []).map((c: any) => ({
          value: c._id || c.id,
          label: c.name || c.contractorId || c._id,
        }))
        setContractorOptions(list)
      })
      .catch(() => setContractorOptions([]))
  }, [])

  const fetchAllData = useCallback(async () => {
    if (!(currentSession as any)?._id) return
    setLoading(true)
    try {
      const [invoicesResponse, summaryData] = await Promise.all([
        getTransportInvoices({
          page: pagination.page,
          limit: pagination.limit,
          status: appliedFilters?.status,
          class: appliedFilters?.class,
          system: appliedFilters?.system,
          sessionId: appliedFilters?.session,
          branch: appliedFilters?.branch,
          startDate: appliedFilters?.startDate,
          endDate: appliedFilters?.endDate,
          contractorId: appliedFilters?.contractor || undefined,
          sortBy: sortBy.value.sortBy,
          sortOrder: sortBy.value.sortOrder,
        }),
        getTransportInvoiceSummary({
          sessionId: (currentSession as any)._id,
          status: appliedFilters?.status,
          class: appliedFilters?.class,
          system: appliedFilters?.system,
          branch: appliedFilters?.branch,
          dateFrom: appliedFilters?.startDate,
          dateTo: appliedFilters?.endDate,
          contractorId: appliedFilters?.contractor || undefined,
        }),
      ])

      if (invoicesResponse.success) {
        setInvoices(invoicesResponse.data || [])
        setTotalCount(invoicesResponse.pagination.total || 0)
        setPaginationData({
          currentPage: invoicesResponse.pagination.currentPage,
          hasNextPage: invoicesResponse.pagination.hasNextPage,
          hasPrevPage: invoicesResponse.pagination.hasPrevPage,
          limit: invoicesResponse.pagination.limit,
          total: invoicesResponse.pagination.total,
          totalPages: invoicesResponse.pagination.totalPages,
        })
        setSummary(summaryData as TransportInvoiceSummary)
      }
    } catch (error) {
      console.error("Error fetching transport invoices:", error)
      setInvoices([])
      setSummary(DEFAULT_SUMMARY)
    } finally {
      setLoading(false)
    }
  }, [pagination, appliedFilters, sortBy, (currentSession as any)?._id])

  const handleCancelInvoice = async () => {
    setCancellingInvoice(true)
    try {
      await cancelTransportInvoice(deleteInvoiceId)
      setDeleteInvoiceId("")
      await fetchAllData()
      toast.success("Transport invoice cancelled successfully!")
    } catch (error) {
      console.error("Error cancelling invoice:", error)
      toast.error("Failed to cancel invoice. Please try again.")
    } finally {
      setCancellingInvoice(false)
    }
  }

  const handlePrint = async () => {
    if (!totalCount) { toast.warning("No data to print"); return }
    setPrintLoading(true)
    try {
      const all = await fetchAllForExport()
      printTable("Transport Invoices", EXPORT_HEADERS, toExportRows(all), {
        logoUrl: currentBranch?.blackLogoUrl,
        brandName: campusBrandName(currentBranch),
        branchLabel: campusBranchLabel(currentBranch),
      })
    } catch {
      toast.error("Failed to prepare print data")
    } finally {
      setPrintLoading(false)
    }
  }

  const handleExportPdf = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setPdfLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToPdf("Transport Invoices", EXPORT_HEADERS, toExportRows(all), "transport-invoices.pdf", "landscape")
    } catch {
      toast.error("Failed to export PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setExcelLoading(true)
    try {
      const all = await fetchAllForExport()
      const rows = toExportRows(all).map(row =>
        Object.fromEntries(EXPORT_HEADERS.map((h, i) => [h, row[i]]))
      )
      exportTableToExcel(rows, "Transport Invoices", "transport-invoices.xlsx")
    } catch {
      toast.error("Failed to export Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  function mapTransportInvoiceToTableData(inv: any): InvoiceTableData {
    const normalizedInvoiceItems = (inv.invoiceItems ?? []).map((item: any) => ({
      ...item,
      feeGroup:
        typeof item?.feeGroup === "object" && item?.feeGroup?.name
          ? item.feeGroup
          : { name: inv?.feeGroupId?.name || "Transport" },
      description:
        item?.description ||
        `${inv?.feeGroupId?.name || "Transport"} Fee${item?.month ? ` - ${item.month}` : ""}`,
    }))

    const totalItemDiscount = normalizedInvoiceItems.reduce(
      (sum: number, item: any) => sum + Number(item?.discount || 0),
      0
    )

    return {
      _id: inv._id,
      invoiceNumber: inv.invoiceNumber || inv._id,
      status: inv.status || "unpaid",
      total: inv.total ?? 0,
      subTotal: inv.subTotal ?? 0,
      fine: inv.fine ?? 0,
      discount: { profile: 0, manual: totalItemDiscount },
      invoiceItems: normalizedInvoiceItems,
      student: inv.student
        ? {
            _id: inv.student._id,
            grNumber: inv.student.grNumber,
            user: inv.student.userId
              ? {
                  _id: inv.student.userId._id,
                  firstName: inv.student.userId.firstName,
                  lastName: inv.student.userId.lastName,
                } as any
              : undefined,
          } as any
        : undefined,
      class: inv.class ? { _id: inv.class._id, name: inv.class.name } as any : ({ name: "–" } as any),
      section: inv.section as any,
      branch: inv.branch || "",
      session: inv.session || "",
      system: inv.system || "",
      isDeleted: false,
      createdBy: inv.createdBy ?? null,
      createdAt: inv.createdAt || "",
      updatedAt: inv.updatedAt || "",
    }
  }

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page })
  }

  const handleSystemChange = (systemItem: any) => {
    setAppliedFilters((prev) => ({ ...prev, system: systemItem?._id }))
  }

  const handleContractorChange = (opt: Option | null) => {
    setAppliedFilters(prev => ({ ...prev, contractor: opt?.value as string || "" }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSortSelect = (item: { label: string; value: { sortBy: string; sortOrder: string } }) => {
    setSortBy(item)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleRefresh = async () => {
    await fetchAllData()
  }

  const printRef = useRef<HTMLDivElement>(null)

  const handlePrintReceipt = () => {
    const content = printRef.current
    if (!content) return
    const docTitle = `Transport-Fee-Receipt-${selectedInvoice?.invoiceNumber || ""}`
    const win = window.open("", "_blank")
    if (!win) return
    win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>${docTitle}</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #111; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
      .text-end { text-align: right; }
      .text-center { text-align: center; }
      .fw-bold { font-weight: bold; }
      .text-uppercase { text-transform: uppercase; }
      h4 { font-size: 16px; margin-bottom: 4px; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>${content.innerHTML}</body>
</html>`)
    win.document.close()
    win.focus()
    win.print()
    win.close()
    setShowInvoiceModal(false)
    setSelectedInvoice(null)
  }

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false)
    setSelectedInvoice(null)
  }

  const getStudentClassName = (invoice: any) =>
    invoice?.class?.name ??
    invoice?.student?.class?.name ??
    invoice?.student?.currentEnrollment?.class?.name ??
    "–"

  const getStudentSectionName = (invoice: any) =>
    invoice?.section?.name ??
    invoice?.student?.section?.name ??
    invoice?.student?.currentEnrollment?.section?.name ??
    "–"

  const handlePrintInvoice = async (record: any) => {
    let invoiceForPrint = record
    const studentId = record?.student?._id
    const sessionId = appliedFilters?.session || (currentSession as any)?._id

    if (studentId && sessionId) {
      try {
        const transportInfo = await getTransportRemainingMonths(studentId, sessionId)
        const currentEnrollment = transportInfo?.data?.currentEnrollment
        const studentInfo = transportInfo?.data?.student

        invoiceForPrint = {
          ...record,
          class: currentEnrollment?.class ?? studentInfo?.class ?? null,
          section: currentEnrollment?.section ?? studentInfo?.section ?? null,
          student: {
            ...record?.student,
            class: studentInfo?.class ?? null,
            section: studentInfo?.section ?? null,
            currentEnrollment: currentEnrollment ?? null,
          },
        }
      } catch (error) {
        console.error("Error fetching transport student class details:", error)
      }
    }

    setSelectedInvoice(invoiceForPrint)
    setShowInvoiceModal(true)
  }

  const calculateTotalDiscount = (record: any) => {
    if (!Array.isArray(record.invoiceItems)) return 0
    return record.invoiceItems.reduce(
      (sum: number, item: any) => sum + Number(item?.discount || 0),
      0
    )
  }

  const calculateFinalAmount = (record: any) => {
    const subTotal = record.subTotal || 0
    const fine = record.fine || 0
    const totalDiscount = calculateTotalDiscount(record)
    return subTotal - totalDiscount + fine
  }

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      render: (text: string, record: any) => (
        <Link to="#" className="link-primary" onClick={() => handlePrintInvoice(record)}>
          {record.invoiceNumber || record._id || "–"}
        </Link>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "studentName",
      render: (_: string, record: any) => {
        const name = record.student?.userId
          ? `${record.student.userId.firstName || ""} ${record.student.userId.lastName || ""}`.trim()
          : "N/A"
        return (
          <Link to="#" onClick={() => handlePrintInvoice(record)}>
            {name || "N/A"}
          </Link>
        )
      },
    },
    {
      title: "GR No",
      dataIndex: "grNumber",
      render: (_: string, record: any) => (
        <span>{record.student?.grNumber ?? "–"}</span>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      render: (_: any, record: any) => (
        <span>{record.contractor?.name || "–"}</span>
      ),
    },
    {
      title: "Fees Group",
      dataIndex: "feeGroupId",
      render: (_: any, record: any) => (
        <span>{record.feeGroupId?.name || "Transport"}</span>
      ),
    },
    {
      title: "Month",
      key: "month",
      render: (_: any, record: any) => {
        const month = record.month
        if (month && month !== "other") {
          return dayjs(month).isValid() ? dayjs(month).format("MMMM YYYY") : month
        }
        const items = record.invoiceItems
        if (Array.isArray(items) && items.length > 0) {
          const formatted = items
            .map((item: any) => {
              const m = item.month
              if (!m || m === "other") return null
              return dayjs(m).isValid() ? dayjs(m).format("MMMM YYYY") : m
            })
            .filter(Boolean)
          return formatted.length > 0 ? formatted.join(", ") : "–"
        }
        return "–"
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (createdAt: string) => (createdAt ? new Date(createdAt).toLocaleDateString() : "–"),
    },
    { title: "Sub Total (RS)", dataIndex: "subTotal", render: (v: number) => v ?? "–" },
    {
      title: "Discount (RS)",
      dataIndex: "discount",
      render: (_: any, record: any) => calculateTotalDiscount(record) || "–",
    },
    { title: "Paid Amount (RS)", dataIndex: "total", render: (v: number) => v ?? "–" },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        let badgeClass = "badge-soft-info"
        if (status === "paid") badgeClass = "badge-soft-success"
        if (status === "Overdue") badgeClass = "badge-soft-warning"
        if (status === "unpaid") badgeClass = "badge-soft-danger"
        if (status === "cancelled") badgeClass = "badge-soft-secondary"
        return (
          <span className={`badge d-inline-flex align-items-center ${badgeClass}`}>
            <i className="ti ti-circle-filled fs-5 me-1" />
            {status || "–"}
          </span>
        )
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: any) => {
        const disabled = record.status === "cancelled"
        const showCancelBtn =
          isSuperAdmin(user) ||
          user?.permissions?.includes("invoices.delete") ||
          user?.permissions?.includes("invoice.delete") ||
          user?.permissions?.includes("transport.delete") ||
          user?.permissions?.includes("transportation-fee.delete")

        return (
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm ms-2"
              onClick={() => handlePrintInvoice(record)}
            >
              Print
            </button>
            {showCancelBtn && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => setDeleteInvoiceId(record._id)}
                disabled={disabled}
                data-bs-toggle="modal"
                data-bs-target="#delete-transport-invoice-modal"
              >
                <i className="ti ti-trash me-1" />
                Cancel
              </button>
            )}
          </div>
        )
      },
    },
  ]

  const selectedForPrint = selectedInvoice
    ? mapTransportInvoiceToTableData(selectedInvoice)
    : null
  const totalAmount =
    summary.totalAmount ??
    summary.feeBreakdown.reduce((sum, item) => sum + Number(item.amount || 0), 0)

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Transport Invoices</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Transport</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Transport Invoices
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={handleRefresh}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Transport Invoices List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <PredefinedDateRanges
                    key={(currentSession as any)?._id}
                    minDate={currentSession?.startDate ? new Date(currentSession.startDate) : undefined}
                    maxDate={currentSession?.endDate ? new Date(currentSession.endDate) : undefined}
                    defaultStartDate={currentSession?.startDate ? new Date(currentSession.startDate) : undefined}
                    defaultEndDate={currentSession?.endDate ? new Date(currentSession.endDate) : undefined}
                    onDateSelect={({ startDate, endDate }) => {
                      setAppliedFilters((prev) => ({ ...prev, startDate, endDate }))
                      setPagination((prev) => ({ page: 1, limit: prev.limit }))
                    }}
                  />
                </div>
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find((sys: any) => sys._id === appliedFilters?.system)}
                    getOptionValue={(item: any) => item._id}
                    getOptionLabel={(item: any) => item.name}
                    onChange={handleSystemChange}
                    placeholder="Select System"
                  />
                </div>
                <div className="mb-3 me-2" style={{ minWidth: 190 }}>
                  <CommonSelect
                    className="select"
                    options={allContractorOptions}
                    value={allContractorOptions.find(o => o.value === (appliedFilters?.contractor ?? "")) ?? ALL_CONTRACTOR}
                    onChange={handleContractorChange}
                    placeholder="All Contractors"
                  />
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {TRANSPORT_SORT_OPTIONS.map((item: any, index: number) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {process.env.REACT_APP_SKIP_VISIBLE_FLAG && (
              <div className="card mb-4">
                <div className="card-header">
                  <h4 className="card-title mb-0">Transport Fee Summary</h4>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading summary...</p>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
                        <div className="card border hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-cash-banknote text-primary fs-1" />
                            </div>
                            <h2 className="fw-bold mb-1 text-primary">
                              {totalAmount.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">Total Amount</p>
                            <small className="text-muted">Total transport invoice amount</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
                        <div className="card border shadow-sm hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-discount text-success fs-1" />
                            </div>
                            <h2 className="fw-bold mb-1 text-success">
                              {summary.totalDiscount.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">Total Discount</p>
                            <small className="text-muted">Discount given on invoices</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
                        <div className="card border shadow-sm hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-wallet text-secondary fs-1" />
                            </div>
                            <h2 className="fw-bold mb-1 text-secondary">
                              {summary.totalCollected.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">Total Transport Fee</p>
                            <small className="text-muted">Total transport fee collected</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="card-body p-0 py-3">
              <Table
                dataSource={invoices}
                columns={columns}
                loading={loading || cancellingInvoice}
                setSearchText={setSearchText}
                pagination={{
                  ...paginationData,
                  onChange: handleChange,
                }}
              />
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h4 className="card-title mb-0">Transport Fee Breakdown</h4>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>Month</th>
                      {summary.monthBreakdownData.map((item, idx) => (
                        <th key={`month-${idx}`} className="text-center">
                          {item.month}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">Amount</td>
                      {summary.monthBreakdownData.map((item, idx) => (
                        <td key={`amount-${idx}`} className="text-center">
                          {Number(item.amount).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <ConfirmationModal
            modalId="delete-transport-invoice-modal"
            title="Confirm Cancellation"
            desc="You want to cancel the selected transport invoice. This cannot be undone once cancelled."
            confirmationBtnText="Yes, Cancel"
            cancelBtnText="Cancel"
            onConfirm={handleCancelInvoice}
          />

          <TransportInvoicePrintModal
            show={showInvoiceModal}
            onClose={handleCloseInvoiceModal}
            onPrint={handlePrintReceipt}
            invoice={selectedForPrint}
            studentDetails={{
              firstName: selectedInvoice?.student?.userId?.firstName ?? "",
              lastName: selectedInvoice?.student?.userId?.lastName ?? "",
              className: getStudentClassName(selectedInvoice),
              grNumber: selectedInvoice?.student?.grNumber ?? "",
              section: getStudentSectionName(selectedInvoice),
            }}
            printRef={printRef}
            calculateFinalAmount={calculateFinalAmount}
          />
        </div>
      </div>
    </div>
  )
}

export default TransportInvoices
